import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CardDescription } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Eye, EyeOff, Trash2, Pencil, Users, Copy, Check } from "lucide-react";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeachersPage() {
  const [showBulkImport, setShowBulkImport] = useState(false);
  // Bulk Add Teachers state
  const [bulkTeachers, setBulkTeachers] = useState([
    { id: 1, full_name: '', email: '', phone_number: '', national_id: '', class_id: '', physical_address: '', assigned_subjects: '', date_of_birth: '', spouse_name: '', spouse_phone: '' },
    { id: 2, full_name: '', email: '', phone_number: '', national_id: '', class_id: '', physical_address: '', assigned_subjects: '', date_of_birth: '', spouse_name: '', spouse_phone: '' },
    { id: 3, full_name: '', email: '', phone_number: '', national_id: '', class_id: '', physical_address: '', assigned_subjects: '', date_of_birth: '', spouse_name: '', spouse_phone: '' },
    { id: 4, full_name: '', email: '', phone_number: '', national_id: '', class_id: '', physical_address: '', assigned_subjects: '', date_of_birth: '', spouse_name: '', spouse_phone: '' },
    { id: 5, full_name: '', email: '', phone_number: '', national_id: '', class_id: '', physical_address: '', assigned_subjects: '', date_of_birth: '', spouse_name: '', spouse_phone: '' },
  ]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [revealedIds, setRevealedIds] = useState({});
  const [copiedField, setCopiedField] = useState(null);
  const [newTeacherCredentials, setNewTeacherCredentials] = useState(null);
  const [formData, setFormData] = useState({
    full_name: "",
    national_id: "",
    email: "",
    phone_number: "",
    physical_address: "",
    assigned_subjects: "",
    class_id: "",
    date_of_birth: "",
    spouse_name: "",
    spouse_phone: "",
  });

  useEffect(() => {
    fetchTeachers();
    fetchClasses();
  }, []);

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API}/teachers`);
      setTeachers(response.data);
    } catch (error) {
      toast.error("Failed to fetch teachers");
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(response.data);
    } catch (error) {
      toast.error("Failed to fetch classes");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        assigned_subjects: formData.assigned_subjects
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        class_id: formData.class_id || null,
      };

      if (editingTeacher) {
        await axios.put(`${API}/teachers/${editingTeacher.id}`, payload);
        toast.success("Teacher updated successfully");
        setIsModalOpen(false);
      } else {
        const response = await axios.post(`${API}/teachers`, payload);
        // Show credentials in a modal
        setNewTeacherCredentials({
          name: payload.full_name,
          username: response.data.username,
          password: response.data.default_password,
          email: payload.email,
        });
        toast.success("Teacher added successfully!");
      }

      resetForm();
      fetchTeachers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      full_name: teacher.full_name,
      national_id: teacher.national_id,
      email: teacher.email,
      phone_number: teacher.phone_number,
      physical_address: teacher.physical_address,
      assigned_subjects: teacher.assigned_subjects.join(", "),
      class_id: teacher.class_id || "",
      date_of_birth: teacher.date_of_birth || "",
      spouse_name: teacher.spouse_name || "",
      spouse_phone: teacher.spouse_phone || "",
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this teacher?")) return;
    try {
      await axios.delete(`${API}/teachers/${id}`);
      toast.success("Teacher deleted successfully");
      fetchTeachers();
    } catch (error) {
      toast.error("Failed to delete teacher");
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: "",
      national_id: "",
      email: "",
      phone_number: "",
      physical_address: "",
      assigned_subjects: "",
      class_id: "",
      date_of_birth: "",
      spouse_name: "",
      spouse_phone: "",
    });
    setEditingTeacher(null);
  };

  const toggleReveal = (id) => {
    setRevealedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const maskNationalId = (id) => {
    if (id.length <= 4) return id;
    return "***" + id.slice(-4);
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div data-testid="teachers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 flex items-center gap-3">
            <Users className="text-pink-500" size={28} />
            Teacher Directory
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Manage teacher information and credentials
          </p>
        </div>

        <Dialog open={isModalOpen} onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button
              className="bg-pink-500 hover:bg-pink-600 text-white btn-press"
              data-testid="add-teacher-btn"
            >
              <Plus size={18} className="mr-2" />
              Add Teacher
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingTeacher ? "Edit Teacher" : "Add New Teacher"}
              </DialogTitle>
              <DialogDescription>
                {editingTeacher
                  ? "Update teacher information"
                  : "Fill in the details to add a new teacher"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  placeholder="John Doe"
                  required
                  data-testid="teacher-fullname"
                />
              </div>
              <div className="space-y-2">
                <Label>National ID</Label>
                <Input
                  value={formData.national_id}
                  onChange={(e) =>
                    setFormData({ ...formData, national_id: e.target.value })
                  }
                  placeholder="123456789"
                  required
                  data-testid="teacher-national-id"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="john@cresabel.edu"
                  required
                  data-testid="teacher-email"
                />
                {!editingTeacher && (
                  <p className="text-xs text-slate-500">
                    Username will be the part before @ (e.g., "john")
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input
                  value={formData.phone_number}
                  onChange={(e) =>
                    setFormData({ ...formData, phone_number: e.target.value })
                  }
                  placeholder="+1234567890"
                  required
                  data-testid="teacher-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Physical Address</Label>
                <Input
                  value={formData.physical_address}
                  onChange={(e) =>
                    setFormData({ ...formData, physical_address: e.target.value })
                  }
                  placeholder="123 School Street, City"
                  required
                  data-testid="teacher-address"
                />
              </div>
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) =>
                    setFormData({ ...formData, date_of_birth: e.target.value })
                  }
                  data-testid="teacher-dob"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Spouse Name</Label>
                  <Input
                    value={formData.spouse_name}
                    onChange={(e) =>
                      setFormData({ ...formData, spouse_name: e.target.value })
                    }
                    placeholder="Spouse full name"
                    data-testid="teacher-spouse-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Spouse Phone</Label>
                  <Input
                    value={formData.spouse_phone}
                    onChange={(e) =>
                      setFormData({ ...formData, spouse_phone: e.target.value })
                    }
                    placeholder="+263..."
                    data-testid="teacher-spouse-phone"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Assigned Subjects (comma-separated)</Label>
                <Input
                  value={formData.assigned_subjects}
                  onChange={(e) =>
                    setFormData({ ...formData, assigned_subjects: e.target.value })
                  }
                  placeholder="Mathematics, Physics, Chemistry"
                  data-testid="teacher-subjects"
                />
              </div>
              <div className="space-y-2">
                <Label>Assigned Class</Label>
                <Select
                  value={formData.class_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, class_id: value })
                  }
                >
                  <SelectTrigger data-testid="teacher-class">
                    <SelectValue placeholder="Select a class (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>
                        {cls.display_name || `${cls.grade_level} ${cls.name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!editingTeacher && (
                <div className="p-3 bg-pink-50 rounded-lg border border-pink-100">
                  <p className="text-sm text-pink-700">
                    <strong>Note:</strong> Password will be set to the teacher's first name (lowercase).
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-pink-500 hover:bg-pink-600"
                  data-testid="save-teacher-btn"
                >
                  {editingTeacher ? "Update" : "Add"} Teacher
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Collapsible Bulk Add Teachers */}
      <div className="mb-8">
        <Button
          variant="outline"
          className="mb-2"
          onClick={() => setShowBulkImport((v) => !v)}
        >
          {showBulkImport ? 'Hide Bulk Add Teachers' : 'Bulk Add Teachers'}
        </Button>
        {showBulkImport && (
          <Card className="border-slate-200 mb-4">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Bulk Add Teachers</CardTitle>
              <CardDescription>Fill in the teacher information. Fields marked with * are required.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-700 min-w-[150px]">Full Name *</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[160px]">Email *</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[130px]">Phone Number</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[120px]">National ID</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[120px]">Class</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[160px]">Assigned Subjects</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[150px]">Physical Address</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[120px]">Date of Birth</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[130px]">Spouse Name</TableHead>
                      <TableHead className="font-medium text-slate-700 min-w-[120px]">Spouse Phone</TableHead>
                      <TableHead className="font-medium text-slate-700 w-[50px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bulkTeachers.map((teacher, idx) => (
                      <TableRow key={teacher.id} className="table-row-hover">
                        <TableCell>
                          <Input
                            placeholder="e.g., John Doe"
                            value={teacher.full_name}
                            onChange={e => setBulkTeachers(ts => ts.map((t, i) => i === idx ? { ...t, full_name: e.target.value } : t))}
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            placeholder="email@example.com"
                            type="email"
                            value={teacher.email}
                            onChange={e => setBulkTeachers(ts => ts.map((t, i) => i === idx ? { ...t, email: e.target.value } : t))}
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            placeholder="+263..."
                            value={teacher.phone_number}
                            onChange={e => setBulkTeachers(ts => ts.map((t, i) => i === idx ? { ...t, phone_number: e.target.value } : t))}
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            placeholder="e.g., 63-123456A78"
                            value={teacher.national_id}
                            onChange={e => setBulkTeachers(ts => ts.map((t, i) => i === idx ? { ...t, national_id: e.target.value } : t))}
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={teacher.class_id}
                            onValueChange={value => setBulkTeachers(ts => ts.map((t, i) => i === idx ? { ...t, class_id: value } : t))}
                          >
                            <SelectTrigger className="text-sm border-slate-200 focus:border-pink-500">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {classes.map(cls => (
                                <SelectItem key={cls.id} value={cls.id.toString()}>{cls.display_name || `${cls.grade_level} ${cls.name}`}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            placeholder="Math, English, ..."
                            value={teacher.assigned_subjects}
                            onChange={e => setBulkTeachers(ts => ts.map((t, i) => i === idx ? { ...t, assigned_subjects: e.target.value } : t))}
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            placeholder="123 Main St"
                            value={teacher.physical_address}
                            onChange={e => setBulkTeachers(ts => ts.map((t, i) => i === idx ? { ...t, physical_address: e.target.value } : t))}
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="date"
                            value={teacher.date_of_birth}
                            onChange={e => setBulkTeachers(ts => ts.map((t, i) => i === idx ? { ...t, date_of_birth: e.target.value } : t))}
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            placeholder="Spouse name"
                            value={teacher.spouse_name}
                            onChange={e => setBulkTeachers(ts => ts.map((t, i) => i === idx ? { ...t, spouse_name: e.target.value } : t))}
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            placeholder="+263..."
                            value={teacher.spouse_phone}
                            onChange={e => setBulkTeachers(ts => ts.map((t, i) => i === idx ? { ...t, spouse_phone: e.target.value } : t))}
                            className="text-sm border-slate-200 focus:border-pink-500"
                          />
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => setBulkTeachers(ts => ts.length === 1 ? ts : ts.filter((_, i) => i !== idx))}
                            className="text-slate-400 hover:text-red-500 transition-colors p-1"
                            title="Remove row"
                          >
                            <Trash2 size={18} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setBulkTeachers(ts => [...ts, { id: Date.now(), full_name: '', email: '', phone_number: '', national_id: '', class_id: '', physical_address: '', assigned_subjects: '', date_of_birth: '', spouse_name: '', spouse_phone: '' }])}
                  variant="outline"
                  className="flex items-center gap-2 border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  <Plus size={18} /> Add More Teachers
                </Button>
                <Button
                  onClick={async () => {
                    const validTeachers = bulkTeachers.filter(t => t.full_name && t.email);
                    if (validTeachers.length === 0) {
                      toast.error('Please fill in at least the name and email for one teacher');
                      return;
                    }
                    setBulkSubmitting(true);
                    try {
                      const response = await axios.post(`${API}/teachers/bulk-add`, { teachers: validTeachers }, { headers: { 'Content-Type': 'application/json' } });
                      setBulkResult(response.data);
                      toast.success(response.data.message);
                      setBulkTeachers([{ id: 1, full_name: '', email: '', phone_number: '', national_id: '', class_id: '', physical_address: '', assigned_subjects: '', date_of_birth: '', spouse_name: '', spouse_phone: '' }]);
                      fetchTeachers();
                    } catch (error) {
                      toast.error(error.response?.data?.message || 'Failed to add teachers');
                    } finally {
                      setBulkSubmitting(false);
                    }
                  }}
                  disabled={bulkSubmitting}
                  className="bg-pink-500 hover:bg-pink-600 btn-press ml-auto"
                >
                  {bulkSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                      Adding Teachers...
                    </span>
                  ) : (
                    'Add Teachers'
                  )}
                </Button>
              </div>
              {/* Results Section */}
              {bulkResult && (
                <Card className="border-slate-200 mt-6">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Check className="text-emerald-500" size={20} /> Add Results
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-emerald-600 font-medium">{bulkResult.message}</p>
                      {bulkResult.created_teachers && bulkResult.created_teachers.length > 0 && (
                        <div>
                          <h4 className="font-medium text-slate-700 mb-2">Created Teachers ({bulkResult.created_teachers.length}):</h4>
                          <div className="max-h-60 overflow-y-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-slate-50">
                                  <TableHead>Name</TableHead>
                                  <TableHead>Email / Username</TableHead>
                                  <TableHead>Default Password</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {bulkResult.created_teachers.map((teacher, idx) => (
                                  <TableRow key={idx} className="table-row-hover">
                                    <TableCell>{teacher.name}</TableCell>
                                    <TableCell className="font-mono">{teacher.email}</TableCell>
                                    <TableCell className="font-mono">{teacher.default_password}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}
                      {bulkResult.errors && bulkResult.errors.length > 0 && (
                        <div>
                          <h4 className="font-medium text-red-600 flex items-center gap-2 mb-2">
                            <span>Errors ({bulkResult.errors.length}):</span>
                          </h4>
                          <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                            {bulkResult.errors.map((err, idx) => (
                              <p key={idx} className="text-sm text-red-600">{err.error}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      <Dialog open={!!newTeacherCredentials} onOpenChange={(open) => {
        if (!open) setNewTeacherCredentials(null);
      }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-emerald-600">
              Teacher Created Successfully!
            </DialogTitle>
            <DialogDescription>
              Share these login credentials with {newTeacherCredentials?.name}
            </DialogDescription>
          </DialogHeader>
          {newTeacherCredentials && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-slate-50 rounded-lg border space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">Username</p>
                    <p className="font-mono font-medium text-slate-900">{newTeacherCredentials.username}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(newTeacherCredentials.username, 'username')}
                  >
                    {copiedField === 'username' ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500">Password</p>
                    <p className="font-mono font-medium text-slate-900">{newTeacherCredentials.password}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(newTeacherCredentials.password, 'password')}
                  >
                    {copiedField === 'password' ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>
              <p className="text-sm text-slate-500">
                The teacher should change their password after first login.
              </p>
              <Button
                onClick={() => setNewTeacherCredentials(null)}
                className="w-full bg-pink-500 hover:bg-pink-600"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Teachers Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">
            All Teachers ({teachers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
            </div>
          ) : teachers.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users size={48} className="mx-auto mb-4 opacity-50" />
              <p>No teachers added yet</p>
              <p className="text-sm">Click "Add Teacher" to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="font-medium text-slate-500">Name</TableHead>
                    <TableHead className="font-medium text-slate-500">Username</TableHead>
                    <TableHead className="font-medium text-slate-500">National ID</TableHead>
                    <TableHead className="font-medium text-slate-500">Email</TableHead>
                    <TableHead className="font-medium text-slate-500">Phone</TableHead>
                    <TableHead className="font-medium text-slate-500">Assigned Class</TableHead>
                    <TableHead className="font-medium text-slate-500">Subjects</TableHead>
                    <TableHead className="font-medium text-slate-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teachers.map((teacher) => (
                    <TableRow
                      key={teacher.id}
                      className="table-row-hover"
                      data-testid={`teacher-row-${teacher.id}`}
                    >
                      <TableCell className="font-medium text-slate-900">
                        {teacher.full_name}
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                          {teacher.username || "N/A"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-slate-600 masked-text text-sm">
                            {revealedIds[teacher.id]
                              ? teacher.national_id
                              : maskNationalId(teacher.national_id)}
                          </span>
                          <button
                            onClick={() => toggleReveal(teacher.id)}
                            className="text-slate-400 hover:text-pink-500 transition-colors"
                            data-testid={`reveal-id-${teacher.id}`}
                          >
                            {revealedIds[teacher.id] ? (
                              <EyeOff size={14} />
                            ) : (
                              <Eye size={14} />
                            )}
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{teacher.email}</TableCell>
                      <TableCell className="text-slate-600 text-sm">{teacher.phone_number}</TableCell>
                      <TableCell>
                        {teacher.class_name ? (
                          <Badge className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                            {teacher.class_name}
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-sm">Not assigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {teacher.assigned_subjects.slice(0, 2).map((subject, idx) => (
                            <Badge
                              key={idx}
                              variant="secondary"
                              className="bg-pink-50 text-pink-700 hover:bg-pink-100 text-xs"
                            >
                              {subject}
                            </Badge>
                          ))}
                          {teacher.assigned_subjects.length > 2 && (
                            <Badge variant="secondary" className="bg-slate-100 text-xs">
                              +{teacher.assigned_subjects.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(teacher)}
                            className="text-slate-500 hover:text-pink-500"
                            data-testid={`edit-teacher-${teacher.id}`}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(teacher.id)}
                            className="text-slate-500 hover:text-red-500"
                            data-testid={`delete-teacher-${teacher.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
